/**
 * ErrorBoundary — catches render-time exceptions in the wrapped subtree and
 * shows a recoverable fallback instead of an unhandled crash unmounting the
 * whole app to a blank page (React error boundaries must be class
 * components — there is no hooks equivalent).
 *
 * track:track-backlog--p0-white-page-when-clicking-project-in-left-nav--requires-browser-refresh
 * — clicking a project in the left nav could hit a null/undefined access in
 * a child component before its data finished loading; with no boundary,
 * that exception unmounted the entire tree and only a browser refresh
 * recovered. The caller passes a `resetKey` that changes whenever the
 * user switches project/view context, so navigating away from the failing
 * view remounts this boundary and clears the error automatically.
 */
import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  resetKey?: string | number;
  fallback?: (error: Error, retry: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

// track:track-followup--stale-chunk-reload — after a deploy, Vite's default
// `emptyOutDir: true` wipes the old hashed chunk files, so a tab left open
// across a deploy 404s the moment it lazy-loads a page it hasn't visited yet
// ("Failed to fetch dynamically imported module" on Chrome/Firefox,
// "Importing a module script failed" on Safari). The plain "Try again"
// button below is useless for this specific error class — it just clears
// state and re-runs the identical broken import(), which fails again every
// time. A real `window.location.reload()` fetches the current index.html
// with correct chunk hashes and fixes it. Guarded by a sessionStorage
// timestamp (survives the reload, tab-scoped) so a reload that doesn't
// actually fix things doesn't loop forever — falls back to a manual
// "Reload page" button instead if we already tried within the last 10s.
const STALE_CHUNK_RELOAD_AT_KEY = "relay-stale-chunk-reload-at";
const STALE_CHUNK_RELOAD_COOLDOWN_MS = 10_000;

function isStaleChunkError(error: Error): boolean {
  const msg = error.message.toLowerCase();
  return msg.includes("dynamically imported module") || msg.includes("importing a module script failed");
}

function recentlyAttemptedStaleChunkReload(): boolean {
  if (typeof window === "undefined") return false;
  const lastAttempt = Number(window.sessionStorage.getItem(STALE_CHUNK_RELOAD_AT_KEY) || "0");
  return Date.now() - lastAttempt < STALE_CHUNK_RELOAD_COOLDOWN_MS;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error("[Relay] ErrorBoundary caught:", error, info.componentStack);
    if (isStaleChunkError(error) && typeof window !== "undefined" && !recentlyAttemptedStaleChunkReload()) {
      window.sessionStorage.setItem(STALE_CHUNK_RELOAD_AT_KEY, String(Date.now()));
      window.location.reload();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  retry = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.retry);

      if (isStaleChunkError(error)) {
        if (!recentlyAttemptedStaleChunkReload()) {
          // componentDidCatch (which runs after this render commits) is
          // about to fire a reload — show a lightweight loading state
          // instead of an alarming error message while it does.
          return (
            <div style={{ padding: 24, fontFamily: "var(--font-ui)", fontSize: 13, color: "var(--ink-soft)" }}>
              Loading the latest version…
            </div>
          );
        }
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 10,
              padding: 24,
              fontFamily: "var(--font-ui)",
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
              A newer version of Relay is available.
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-soft)" }}>
              Reloading didn't pick it up automatically — please reload the page manually.
            </div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: "6px 14px",
                border: "1px solid var(--teal)",
                borderRadius: 6,
                background: "transparent",
                color: "var(--teal-dark)",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "var(--font-ui)",
              }}
            >
              Reload page
            </button>
          </div>
        );
      }

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 10,
            padding: 24,
            fontFamily: "var(--font-ui)",
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
            Something went wrong rendering this view.
          </div>
          <div style={{ fontSize: 12, color: "var(--ink-soft)" }}>{error.message}</div>
          <button
            type="button"
            onClick={this.retry}
            style={{
              padding: "6px 14px",
              border: "1px solid var(--teal)",
              borderRadius: 6,
              background: "transparent",
              color: "var(--teal-dark)",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--font-ui)",
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
